/**
 * ⚠️ Status: In Development - Adoption Guidelines
 *
 * While you can start adopting these new color tokens, please be aware:
 * - Breaking changes may occur as we refine the system
 * - Component-level updates (buttons, inputs, etc.) are pending
 * - Some inconsistencies may exist during transition
 *
 * If adopting now:
 * - Test heavily in your specific use cases
 * - Refer to designs or ask design for intended usage
 * - Expect component-level updates for fixes
 * - Report any issues you encounter
 *
 * Recent changes:
 * - Removed unused colors
 * - Added comprehensive documentation
 * - Component updates pending
 */

/**
 * Color system for the P0 application.
 *
 * This is the future color system setup that will eventually replace variables.ts.
 * This system follows a semantic naming convention and provides both light and dark theme variants.
 *
 * Usage guidelines:
 * - Brand: Primary brand colors used for logos and brand identity
 * - Fill: Background colors for different surfaces and elements
 * - Action: Interactive elements like buttons and links
 * - Text: Typography and content colors
 * - Border: Border and divider colors
 * - Status: System feedback and state indicators
 * - Data Visualization: Charts and graphs
 * - Effects: Visual effects like glass morphism and gradients
 *
 * Each color is available in both light and dark theme variants.
 */

export const colors = {
  // Brand colors
  brand: {
    light: "#00AB8E",
    dark: "#87D3C6",
  },

  // Fill colors for surfaces
  fill: {
    surface: {
      light: "#FFFFFF",
      dark: "#1F2222",
    },
    background: {
      light: "#E7F5F3",
      dark: "#000000",
    },
    glass: {
      light: "rgba(255, 255, 255, 0.7)",
      dark: "rgba(31, 34, 34, 0.7)",
    },
    // Use to render background for highlighted text
    // Balances visibility in accessibility scenarios with avoiding overbearing intensity
    highlight: "rgba(255, 255, 127, 1.0)",
  },

  // Visual effects
  effects: {
    glassBlur: "backdrop-filter: blur(8px)",
    gradients: {
      subtle: "linear-gradient(135deg, #FFFFFF 0%, #E8F5FF 50%, #E7F5F3 100%)",
      brand: "linear-gradient(135deg, #00AB8E 0%, #87D3C6 100%)",
    },
  },

  // Action colors for interactive elements
  action: {
    primary: {
      default: {
        light: "#000000",
        dark: "#FFFFFF",
      },
      hover: {
        light: "#414141",
        dark: "#D3D3D3",
      },
      focused: {
        light: "#000000",
        dark: "#FFFFFF",
      },
    },
    secondary: {
      default: {
        light: "#FFFFFF",
        dark: "#000000",
      },
      hover: {
        light: "#E7F5F3",
        dark: "#282928",
      },
      focused: {
        light: "#E1F0EE",
        dark: "#282928",
      },
    },
  },

  // Text colors
  text: {
    emphasis: {
      light: "#000000",
      dark: "#FFFFFF",
    },
    deemphasis: {
      light: "#575757",
      dark: "#9B9B9B",
    },
    placeholder: {
      light: "#757575",
      dark: "#767575",
    },
  },

  // Border colors
  border: {
    emphasis: {
      light: "#757575",
      dark: "#757575",
    },
    deemphasis: {
      light: "#E3E3E3",
      dark: "#323232",
    },
  },

  // Status colors for system feedback
  status: {
    critical: {
      light: "#A90000",
      dark: "#FF6464",
    },
    error: {
      light: "#D93D31",
      dark: "#F1855A",
    },
    warning: {
      light: "#FF8C00",
      dark: "#FFE683",
    },
    info: {
      light: "#0071EA",
      dark: "#81BAF7",
    },
    success: {
      light: "#16884B",
      dark: "#6DCD99",
    },
  },

  // Data visualization colors
  dataViz: {
    viz1: {
      light: "#9C27B0",
      dark: "#C1A2C7",
    },
    viz2: {
      light: "#009688",
      dark: "#7AA4A0",
    },
    viz3: {
      light: "#ED41A8",
      dark: "#ED93C9",
    },
    viz4: {
      light: "#795548",
      dark: "#CFA291",
    },
    viz5: {
      light: "#1E4161",
      dark: "#A7B7C6",
    },
  },
};

// Example usage:
// import { colors } from 'styles/colors';
//
// styled.div`
//   background-color: ${colors.fill.surface.light};
//   color: ${colors.text.emphasis.light};
// `;
//
// For Ant Design theme configuration:
// token: {
//   colorPrimary: colors.brand.light,
// }
